import { Body, Button, Form, Spacer, validators } from '@spritz-finance/ui'
import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { useSignUp } from '../../app/auth'
import {
    AuthenticationBottomLink,
    AuthenticationBottomText,
    AuthenticationContainer,
    AuthenticationForm,
    AuthenticationTerms,
    Link,
} from '../AuthenticationStyled'
import Divider from '../Divider'
import GoogleButton from '../GoogleButton/GoogleButton'
import { PublicLayoutHeader } from '../PublicLayout/Header'
import ConfirmEmail from './ConfirmEmail'
import { TermsAndConditionsDialog } from './termsAndConditions/TermsAndConditionsDialog'
import styled from 'styled-components'
import { QuickswapSignUpBanner } from '../../../components/theme/quickswap/QuickswapSignUpBanner'
import { ReferralCode } from './ReferralCode'
import { isProduction } from '../../app/config'

type SignUpFormData = {
    email: string
    password: string
    confirmPassword: string
}

const ReferredWrapper = styled.div`
    background: var(--surface-2, #f2ffdc);
    border-radius: 10px;
    padding: 8px 16px;
`

export default function SignUpPage() {
    const navigate = useNavigate()
    const [formData, setFormData] = useState<SignUpFormData>()
    const [confirming, setConfirming] = useState<boolean>(false)
    const [tocOpened, setTocOpened] = useState<boolean>(false)
    const { signUp, error, loading } = useSignUp()

    const methods = useForm<SignUpFormData>({
        reValidateMode: 'onBlur',
    })

    useEffect(() => {
        if (!error) return
        let field
        switch (error.type) {
            case 'InvalidPasswordException':
                field = 'password'
                break
            default:
                field = 'email'
        }
        methods.setError(field, error)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    const onSubmit: SubmitHandler<SignUpFormData> = async (data) => {
        setFormData(data)
        const result = await signUp(data.email, data.password)
        if (result) {
            setConfirming(true)
        }
    }

    const isReferred = window.location.search.match(/grsf=/)
    const referralRockEnabled = !isProduction()

    return (
        <>
            <PublicLayoutHeader
                title="Welcome!"
                subtitle={
                    confirming
                        ? 'Please verify your email.'
                        : isReferred
                        ? 'Sign up for a Spritz account'
                        : ''
                }
            />

            <AuthenticationContainer>
                {!confirming && (
                    <AuthenticationForm>
                        {referralRockEnabled && !isReferred ? <ReferralCode /> : null}
                        {isReferred && (
                            <>
                                <ReferredWrapper>
                                    You’ve been invited by a friend!
                                    <br />
                                    Sign up for a Spritz account and get $50 after making a
                                    qualifying bill payment.
                                </ReferredWrapper>
                                <Spacer height={32} />
                            </>
                        )}
                        <QuickswapSignUpBanner />

                        <GoogleButton label="Sign Up With Google" />

                        <Divider />

                        <Form<SignUpFormData> onSubmit={onSubmit} methods={methods} width="auto">
                            <Form.Input
                                name="email"
                                placeholder="Email address"
                                validation={{
                                    required: true,
                                    validate: {
                                        ...validators.email,
                                    },
                                }}
                            />

                            <Form.Input
                                name="password"
                                type="password"
                                placeholder="Password"
                                autoComplete="off"
                                validation={{ required: true }}
                            />
                            <Form.Input
                                name="confirmPassword"
                                type="password"
                                confirm="password"
                                placeholder="Confirm Password"
                                confirmMessage="Passwords do not match"
                                autoComplete="off"
                            />

                            <AuthenticationTerms>
                                <Body.Reg size={12}>
                                    By signing up and using your account you agree to the Spritz
                                    <Link onClick={() => setTocOpened(true)}>Terms of Service</Link>
                                </Body.Reg>
                            </AuthenticationTerms>

                            <Spacer height={16} />

                            <Button.CTA
                                loading={loading}
                                type="submit"
                                style={{ width: '100%', maxWidth: 'none' }}
                            >
                                Sign Up
                            </Button.CTA>
                        </Form>
                    </AuthenticationForm>
                )}
                {confirming && <ConfirmEmail {...formData!} />}
            </AuthenticationContainer>
            <TermsAndConditionsDialog open={tocOpened} onClose={() => setTocOpened(false)} />

            <Spacer height={48} />

            {!confirming && (
                <AuthenticationBottomText>
                    Already have an account?
                    <AuthenticationBottomLink onClick={() => navigate('/signin')}>
                        Sign in
                    </AuthenticationBottomLink>
                </AuthenticationBottomText>
            )}
        </>
    )
}
